import { FC, useContext } from "react";

import { t } from "i18n/i18n";
import { MenuItem } from "mds/components/Menu";
import { Switch, ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import { BypassReleaseWarningContext } from "providers/BypassReleaseWarningProvider";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";
import { localStateActions } from "store/slices/local";

interface EditModeButtonProps {
  disabledTooltipText?: string;
  isDisabled?: boolean;
  isMenuItem?: boolean;
}

export const EditModeButton: FC<EditModeButtonProps> = ({
  disabledTooltipText,
  isDisabled,
  isMenuItem = false,
}) => {
  const dispatch = useAppDispatch();
  const { resetWarning } = useContext(BypassReleaseWarningContext);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  const toggleEditMode = () => {
    dispatch(localStateActions.setIsEditMode(!isEditModeEnabled));
    resetWarning();
  };

  if (isMenuItem) {
    return (
      <MenuItem eventName="Edit Mode - Toggle" onClick={toggleEditMode}>
        <div className="flex w-full items-center justify-between">
          <span className="mr-2">{t("main_header.edit_mode")}</span>
          <ToggleSwitch isSelected={isEditModeEnabled} onClick={toggleEditMode} />
        </div>
      </MenuItem>
    );
  }

  const button = (
    <RoundedButton
      disabled={isDisabled}
      eventName="Edit Mode - Toggle"
      eventProps={{ isEnabled: !isEditModeEnabled }}
      isSelected={isEditModeEnabled}
      kind="toggle"
      onClick={toggleEditMode}
    >
      {t("main_header.edit_mode")}
      <Switch checked={isEditModeEnabled} disabled={isDisabled} />
    </RoundedButton>
  );

  if (isDisabled && disabledTooltipText) {
    return <Tooltip content={disabledTooltipText}>{button}</Tooltip>;
  }

  return button;
};
